import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package } from 'lucide-react';
import { useProducts } from '../../context/ProductContext';
import { usePagination } from '../../hooks/products/usePagination';
import { ProductCard } from './ProductCard';
import { Pagination } from '../common/Pagination';

interface ProductListProps {
  onProductClick: (id: string) => void;
}

export const ProductList: React.FC<ProductListProps> = ({ onProductClick }) => {
  const { filteredProducts, loading, error } = useProducts();
  const { 
    currentPage, 
    totalPages, 
    paginatedItems, 
    goToPage 
  } = usePagination(filteredProducts);

  if (loading) {
    return (
      <div className="text-center py-12">
        <Package className="mx-auto text-orange-300 animate-spin mb-4" size={48} />
        <p className="text-gray-600">Cargando productos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-500">
        {error}
      </div>
    );
  }

  if (filteredProducts.length === 0) {
    return (
      <div className="text-center py-12 bg-orange-50 rounded-lg">
        <Package className="mx-auto text-orange-300 mb-4" size={48} />
        <p className="text-gray-600">No se encontraron productos</p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-6">
      <div className="flex justify-between items-center text-sm text-gray-600">
        <p>
          Mostrando {paginatedItems.length} de {filteredProducts.length} productos
        </p>
        <p>
          Página {currentPage} de {totalPages}
        </p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <AnimatePresence mode="popLayout">
          {paginatedItems.map((product) => (
            <motion.div
              key={product.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
            >
              <ProductCard
                product={product}
                onClick={() => onProductClick(product.id)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
      )}
    </div>
  );
};